import { Close, FileDownload } from '@/components/icons'
import { CsvTemplateColumnReference } from '@/components/modal/CsvTemplateColumnReference'
import { APP_CUSTOM_ERROR } from '@/constants/MessageConstants'
import { uploadBulkMatchesCSV } from '@/services/club'
import { useGetCsvTemplate } from '@/services/file'
import { downloadCsvTemplate } from '@/utils/csvTemplate'
import { Dialog, Transition } from '@headlessui/react'
import Papa from 'papaparse'
import { Fragment, useState } from 'react'
import Dropzone from 'react-dropzone'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { BulkUploadResults } from './BulkUploadResults'
import { CMButton } from './ui'

type Props = {
  open: boolean
  onClose: () => void
  onSuccess?: () => void
}

interface UploadResult {
  status: 'SUCCESS' | 'FAILURE'
  message?: string
  results?: {
    successCount: number
    failureCount: number
    clubResults: ClubResult[]
  }
  errors?: any
}

interface ClubResult {
  clubId: number
  clubName: string
  status: 'SUCCESS' | 'FAILURE'
  matchesUploaded?: number
  errors?: string[]
}

export const BulkUploadModal = ({ open, onClose, onSuccess }: Props) => {
  const { t } = useTranslation()

  const [file, setFile] = useState<any>()
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const [previewData, setPreviewData] = useState<any[]>([])

  const { data: template, isFetching: templateLoading } = useGetCsvTemplate('bulk-match-import', open)

  const handleDrop = (acceptedFile: any) => {
    setFile(acceptedFile)
    const file = acceptedFile[0]
    setUploadResult(null)

    if (file) {
      Papa.parse(file, {
        complete: (result) => {
          const filterData = result?.data?.filter((item: any) => item.length > 1)
          setPreviewData(filterData.slice(0, 5) as any[]) // Show first 5 rows for preview
        },
        header: false,
        delimiter: ',',
      })
      setShowPreview(true)
    }
  }

  const handleClose = () => {
    onClose()
    setFile([])
    setUploadResult(null)
    setShowPreview(false)
    setPreviewData([])
  }

  const onUpload = async () => {
    if (!file?.length) return

    setIsUploading(true)
    try {
      const res = await uploadBulkMatchesCSV(file[0]) as UploadResult
      setUploadResult(res)
      
      if (res.status === 'SUCCESS') {
        toast.success('Bulk upload completed successfully')
        if (onSuccess) onSuccess()
      } else if (res.status === 'FAILURE') {
        toast.error('Bulk upload failed. Check error details.')
      } else {
        toast.error('Some uploads failed. Check results for details.')
      }
    } catch (error) {
      toast.error(APP_CUSTOM_ERROR)
      setUploadResult({
        status: 'FAILURE',
        message: 'Upload failed due to network error',
      })
    } finally {
      setIsUploading(false)
    }
  }

  const onReUpload = () => {
    setFile([])
    setUploadResult(null)
    setShowPreview(false)
    setPreviewData([])
  }

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full flex flex-col gap-y-4 max-w-4xl transform overflow-hidden bg-white px-6 py-4 text-left align-middle shadow-xl transition-all relative">
                <button
                  className="w-9 h-9 bg-navy-900 bg-opacity-10 flex items-center justify-center ml-2 hover:bg-opacity-20 absolute right-2 top-2"
                  onClick={handleClose}
                >
                  <Close className="text-navy-900" />
                </button>
                <Dialog.Title as="h3" className="text-xl text-navy-900 font-semibold">
                  {t('club.bulkUploadMatches', 'Bulk Upload Matches')}
                </Dialog.Title>

                {!uploadResult ? (
                  <div className="flex flex-col gap-y-6">
                    <div className="flex flex-row gap-x-2">
                      <div className="text-sm text-navy-900 text-opacity-70">
                        <p>
                          {template?.result?.description ??
                            'Upload matches across multiple clubs with a single CSV file. Download the template to see the required format with club identifiers.'}
                        </p>
                      </div>
                      <button
                        className="px-3 py-2 bg-navy-900 bg-opacity-10 hover:bg-opacity-20 hover:transition-all hover:ease-in-out hover:duration-300 text-navy-900 text-sm font-semibold flex gap-x-1 items-center justify-center whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={!template?.result || templateLoading}
                        onClick={() => template?.result && downloadCsvTemplate(template.result)}
                      >
                        <FileDownload className="w-5 h-5 text-navy-900" />
                        {t('common.downloadTemplate', 'Download Template')}
                      </button>
                    </div>
                    <CsvTemplateColumnReference template={template?.result} isLoading={templateLoading} />

                    {!file?.length ? (
                      <Dropzone onDrop={handleDrop}>
                        {({ getRootProps, getInputProps }) => (
                          <div
                            className="flex items-center justify-center bg-navy-900 bg-opacity-10 py-12 cursor-pointer"
                            {...getRootProps()}
                          >
                            <input {...getInputProps()} />
                            <span className="text-xs text-navy-900 font-medium">
                              Drag your bulk CSV file here or <span className="text-primary-500">Browse Files</span>
                            </span>
                          </div>
                        )}
                      </Dropzone>
                    ) : (
                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <p className="text-navy-900 text-opacity-70 text-sm">{file[0]?.name}</p>
                            <p className="text-navy-900 text-opacity-50 text-xs">{(file[0]?.size / 1024).toFixed(1)} KB</p>
                          </div>
                          <button
                            className="text-sm font-semibold text-primary-500 hover:text-opacity-70"
                            onClick={onReUpload}
                          >
                            {t('common.removeFile', 'Remove File')}
                          </button>
                        </div>

                        {showPreview && previewData.length > 0 && (
                          <div className="mb-4">
                            <h4 className="text-sm font-semibold text-navy-900 mb-2">Preview (first 5 rows):</h4>
                            <div className="overflow-x-auto">
                              <table className="w-full text-xs border border-gray-200">
                                <tbody>
                                  {previewData.slice(0, 5).map((row, index) => (
                                    <tr key={index} className={index === 0 ? 'bg-gray-100 font-semibold' : ''}>
                                      {row.map((cell: any, cellIndex: number) => (
                                        <td key={cellIndex} className="border border-gray-200 px-2 py-1 truncate max-w-[100px]">
                                          {cell}
                                        </td>
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}

                        <div className="flex justify-end gap-3">
                          <CMButton variant="muted" size="md" onClick={handleClose}>Cancel</CMButton>
                          <CMButton variant="primary" size="md" onClick={onUpload} disabled={isUploading}>
                            {isUploading ? 'Uploading...' : 'Upload Matches'}
                          </CMButton>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <BulkUploadResults
                    result={uploadResult}
                    onClose={handleClose}
                    onReUpload={onReUpload}
                  />
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}